import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { JournalEntry } from "@/types/journal";

export const useClientJournalEntries = (selectedClientId: string | null) => {
  const [therapistNotes, setTherapistNotes] = useState<{ [entryId: string]: string }>({});

  const { data: journalEntries, isLoading: entriesLoading } = useQuery({
    queryKey: ['client-journal-entries', selectedClientId],
    queryFn: async () => {
      if (!selectedClientId) return [];
      
      const { data: entries, error } = await supabase
        .from('journal_entries')
        .select('*')
        .eq('user_id', selectedClientId)
        .or("shareable_with_therapist.eq.true,shareable_with_therapist.is.null")
        .order('created_at', { ascending: false })
        .range(0, 9999);
        
      if (error) throw error;
      
      try {
        const { data: notes, error: notesError } = await supabase
          .rpc('get_therapist_notes_for_client', {
            client_id_param: selectedClientId
          });
        
        if (!notesError && notes) {
          const notesMap: { [entryId: string]: string } = {};
          notes.forEach((note: any) => {
            notesMap[note.entry_id] = note.content;
          });
          
          setTherapistNotes(notesMap);
        }
      } catch (notesError) {
        console.error("Error fetching notes:", notesError);
      }
      
      return entries as JournalEntry[];
    },
    enabled: !!selectedClientId
  });

  const handleNoteChange = (entryId: string, content: string) => {
    setTherapistNotes(prev => ({ ...prev, [entryId]: content }));
  };

  return {
    journalEntries,
    entriesLoading,
    therapistNotes,
    handleNoteChange
  };
};
